import cancelEvents from "./cancel-events";
import events from "./scroll-events";
import smooth from "./smooth";

/*
 * Gets the easing type from the smooth prop within options.
 */
const getAnimationType = (options: any) => smooth[options.smooth] || smooth.defaultEasing;
/*
 * Function helper
 */
const functionWrapper = (value: any) => (typeof value === "function" ? value : function () { return value; });
/*
 * Wraps window properties to allow server side rendering
 */
const currentWindowProperties = () => {
  if (typeof window !== "undefined") {
    return window.requestAnimationFrame || window.webkitRequestAnimationFrame;
  }
};

/*
 * Helper function to never extend 60fps on the webpage.
 */
const requestAnimationFrameHelper = (() => currentWindowProperties()
    || function (callback: any, element: any, delay: any) {
      window.setTimeout(callback, delay || (1000 / 60), new Date().getTime());
    })();

const makeData = () => ({
  currentPosition: 0,
  startPosition: 0,
  targetPosition: 0,
  progress: 0,
  duration: 0,
  cancel: false,

  target: null,
  containerElement: null,
  to: null,
  start: null,
  delta: null,
  percent: null,
  delayTimeout: null,
});

const currentPositionX = (options: any) => {
  const { containerElement } = options.data;
  if (containerElement && containerElement !== document && containerElement !== document.body) {
    return containerElement.scrollLeft;
  }
  const supportPageOffset = window.pageXOffset !== undefined;
  const isCSS1Compat = ((document.compatMode || "") === "CSS1Compat");
  return supportPageOffset ? window.pageXOffset : isCSS1Compat
    ? document.documentElement.scrollLeft : document.body.scrollLeft;
};

const currentPositionY = (options: any) => {
  const { containerElement } = options.data;
  if (containerElement && containerElement !== document && containerElement !== document.body) {
    return containerElement.scrollTop;
  }
  const supportPageOffset = window.pageXOffset !== undefined;
  const isCSS1Compat = ((document.compatMode || "") === "CSS1Compat");
  return supportPageOffset ? window.pageYOffset : isCSS1Compat
    ? document.documentElement.scrollTop : document.body.scrollTop;
};

const scrollContainerWidth = (options: any) => {
  const { containerElement } = options.data;
  if (containerElement && containerElement !== document && containerElement !== document.body) {
    return containerElement.scrollWidth - containerElement.offsetWidth;
  }
  const { body } = document;
  const html = document.documentElement;

  return Math.max(
    body.scrollWidth,
    body.offsetWidth,
    html.clientWidth,
    html.scrollWidth,
    html.offsetWidth,
  );
};

const scrollContainerHeight = (options: any) => {
  const { containerElement } = options.data;
  if (containerElement && containerElement !== document && containerElement !== document.body) {
    return containerElement.scrollHeight - containerElement.offsetHeight;
  }
  const { body } = document;
  const html = document.documentElement;

  return Math.max(
    body.scrollHeight,
    body.offsetHeight,
    html.clientHeight,
    html.scrollHeight,
    html.offsetHeight,
  );
};

const animateScroll = (easing: any, options: any, timestamp: any) => {
  const { data } = options;

  // Cancel on specific events
  if (!options.ignoreCancelEvents && data.cancel) {
    if (events.registered.end) {
      events.registered.end(data.to, data.target, data.currentPositionY);
    }
    return;
  }

  data.delta = Math.round(data.targetPosition - data.startPosition);

  if (data.start === null) {
    data.start = timestamp;
  }

  data.progress = timestamp - data.start;

  data.percent = (data.progress >= data.duration ? 1 : easing(data.progress / data.duration));

  data.currentPosition = data.startPosition + Math.ceil(data.delta * data.percent);

  if (data.containerElement && data.containerElement !== document && data.containerElement !== document.body) {
    if (options.horizontal) {
      data.containerElement.scrollLeft = data.currentPosition;
    } else {
      data.containerElement.scrollTop = data.currentPosition;
    }
  } else if (options.horizontal) {
    window.scrollTo(data.currentPosition, 0);
  } else {
    window.scrollTo(0, data.currentPosition);
  }

  if (data.percent < 1) {
    const easedAnimate = animateScroll.bind(null, easing, options);
    requestAnimationFrameHelper.call(window, easedAnimate);
    return;
  }

  if (events.registered.end) {
    events.registered.end(data.to, data.target, data.currentPosition);
  }
};

const setContainer = (options: any) => {
  options.data.containerElement = !options
    ? null
    : options.containerId
      ? document.getElementById(options.containerId)
      : options.container && options.container.nodeType
        ? options.container
        : document;
};

const animateTopScroll = (scrollOffset: any, options: any, to: any, target: any) => {
  options.data = options.data || makeData();

  window.clearTimeout(options.data.delayTimeout);

  cancelEvents.subscribe(() => {
    options.data.cancel = true;
  });

  setContainer(options);

  options.data.start = null;
  options.data.cancel = false;
  options.data.startPosition = options.horizontal ? currentPositionX(options) : currentPositionY(options);
  options.data.targetPosition = options.absolute
    ? scrollOffset
    : scrollOffset + options.data.startPosition;

  if (options.data.startPosition === options.data.targetPosition) {
    if (events.registered.end) {
      events.registered.end(options.data.to, options.data.target, options.data.currentPosition);
    }
    return;
  }

  options.data.delta = Math.round(options.data.targetPosition - options.data.startPosition);

  options.data.duration = functionWrapper(options.duration)(options.data.delta);
  options.data.duration = isNaN(parseFloat(options.data.duration)) ? 1000 : parseFloat(options.data.duration);
  options.data.to = to;
  options.data.target = target;

  const easing = getAnimationType(options);
  const easedAnimate = animateScroll.bind(null, easing, options);

  if (options && options.delay > 0) {
    options.data.delayTimeout = window.setTimeout(() => {
      if (events.registered.begin) {
        events.registered.begin(options.data.to, options.data.target);
      }
      requestAnimationFrameHelper.call(window, easedAnimate);
    }, options.delay);
    return;
  }

  if (events.registered.begin) {
    events.registered.begin(options.data.to, options.data.target);
  }
  requestAnimationFrameHelper.call(window, easedAnimate);
};

const proceedOptions = (options: any) => {
  options = { ...options };
  options.data = options.data || makeData();
  options.absolute = true;
  return options;
};

const scrollToTop = (options: any) => {
  animateTopScroll(0, proceedOptions(options));
};

const scrollTo = (toPosition: any, options: any) => {
  animateTopScroll(toPosition, proceedOptions(options));
};

const scrollToBottom = (options: any) => {
  options = proceedOptions(options);
  setContainer(options);

  animateTopScroll(options.horizontal
    ? scrollContainerWidth(options)
    : scrollContainerHeight(options),
  options);
};

const scrollMore = (toPosition: any, options: any) => {
  options = proceedOptions(options);
  setContainer(options);
  const currentPosition = options.horizontal ? currentPositionX(options) : currentPositionY(options);

  animateTopScroll(toPosition + currentPosition, options);
};

export default {
  animateTopScroll,
  getAnimationType,
  scrollToTop,
  scrollToBottom,
  scrollTo,
  scrollMore,
};
